/**
 * ReadOnlyValue — Anzeige eines vom System gesetzten Wertes.
 *
 * Die Abwesenheit eines Input-Felds IST das Read-only-Signal. Kein Lock-Icon noetig.
 * Layout: zweispaltig wie ein <dl> (Label / Value), Wert monospace wenn id-like.
 * Optional Copy-Button am rechten Rand.
 */
import { type ReactNode, useState } from 'react';
import { I } from '@/shared/icons';
import { cn } from '@/shared/lib/cn';

export interface ReadOnlyValueProps {
  label: string;
  value: ReactNode;
  /** Renders value in font-mono (for slugs, UUIDs, hashes). */
  mono?: boolean;
  /** Copy-to-clipboard fuer technische IDs. */
  copyable?: string;
  /** Hint unter dem Wert (z.B. "vor 3 Minuten"). */
  hint?: ReactNode;
  className?: string;
}

export function ReadOnlyValue({
  label,
  value,
  mono = false,
  copyable,
  hint,
  className,
}: ReadOnlyValueProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!copyable) return;
    try {
      await navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard kann blockiert sein — silent fallback
    }
  };

  return (
    <div className={cn('flex items-start justify-between gap-4 py-2', className)}>
      {/*
        Die Beschriftung darf schrumpfen und umbrechen. Mit `shrink-0` drängte eine lange
        Beschriftung den Wert auf null Breite — „Kennung beim Kanalmanager (Objekt)" stand auf
        390 px ohne seinen Wert da, obwohl der Kopieren-Knopf daneben lag.
      */}
      <dt className="text-ink-faint min-w-0 max-w-[55%] pt-0.5 text-xs uppercase tracking-wider">
        {label}
      </dt>
      <dd className="flex min-w-0 flex-1 items-center justify-end gap-2 text-right">
        <div className="min-w-0">
          <div className={cn('text-ink truncate text-sm', mono && 'font-mono text-[13px]')}>
            {value}
          </div>
          {hint && <div className="text-ink-faint mt-0.5 text-[11px]">{hint}</div>}
        </div>
        {copyable && (
          <button
            type="button"
            onClick={handleCopy}
            // 44-px-Trefferfläche nach §9 — das Symbol misst 13 px. Die negativen Ränder halten
            // die Zeilenhöhe unverändert, die Fläche wächst nur nach außen.
            className="text-ink-faint hover:text-ink -my-3 -mr-2 flex size-11 shrink-0 items-center justify-center transition-colors"
            aria-label={copied ? 'Kopiert' : `${label} kopieren`}
          >
            <I name={copied ? 'Check' : 'Copy'} size={13} />
          </button>
        )}
      </dd>
    </div>
  );
}
